/**
 * The customer page's Complaints tab (kanban 2a28d4c0): every quality case
 * and thread on this account with its status and who it waits on, plus
 * "Report a quality complaint" for this customer.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { trackEvent } from '../../../services/portalTracking';
import { getCustomerComplaints, type CustomerComplaint } from '../../../services/portalQualityAsks';
import { formatShortDate } from '../../../utils/dateFormatters';
import { complaintDoorPath, complaintStatusLabel, complaintWaitLabel } from './complaintView';
import ReportComplaintModal from './ReportComplaintModal';
import styles from './Complaints.module.css';

interface CustomerComplaintsTabProps {
  customerNo: string;
  customerName: string | null;
}

export default function CustomerComplaintsTab({ customerNo, customerName }: CustomerComplaintsTabProps) {
  const [items, setItems] = useState<CustomerComplaint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  // Rows link into the quality door only for people who can open it (the
  // same rule as the asks card); others see who has it instead.
  const { hasScope, user } = useAuth();
  const canOpenDoor = Boolean(hasScope?.('quality:copilot')) || user?.role === 'ADMIN';

  // Bumped after a filed complaint; the effect re-reads. State is set only
  // from the fetch's callbacks (never synchronously in the effect body).
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getCustomerComplaints(customerNo)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setError('Could not load complaints for this customer.');
      });
    return () => {
      cancelled = true;
    };
  }, [customerNo, version]);

  return (
    <div className={styles.tab} data-testid="customer-complaints-tab">
      <div className={styles.tabHead}>
        <h3 className={styles.tabTitle}>Quality complaints</h3>
        <button
          type="button"
          className={styles.reportBtn}
          onClick={() => {
            trackEvent('action', 'complaint_report_open', {
              customer_no: customerNo,
              surface: 'customer_detail',
              outcome: 'panel_open',
            });
            setReporting(true);
          }}
        >
          ⚑ Report a quality complaint
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}
      {items === null ? (
        <div className={styles.muted}>Loading…</div>
      ) : items.length === 0 ? (
        <div className={styles.muted}>No quality complaints on this customer.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Ref</th>
              <th>What</th>
              <th>Status</th>
              <th>Waiting on</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={`${item.kind}-${item.ref}`} data-testid="complaint-row">
                <td className={styles.ref}>
                  {canOpenDoor ? (
                    <Link
                      to={complaintDoorPath(item)}
                      className={styles.refLink}
                      onClick={() =>
                        trackEvent('click', 'complaint_row_open', {
                          customer_no: customerNo,
                          ref: item.ref,
                          kind: item.kind,
                          case_ref: item.case_ref ?? null,
                          surface: 'customer_detail',
                          outcome: 'navigate',
                          to: complaintDoorPath(item),
                        })
                      }
                    >
                      {item.ref}
                    </Link>
                  ) : (
                    item.ref
                  )}
                  {item.case_ref && <div className={styles.muted}>case {item.case_ref}</div>}
                  {!canOpenDoor && item.is_open && (
                    <div className={styles.muted} data-testid="complaint-desk-note">
                      the quality desk has it
                    </div>
                  )}
                </td>
                <td>{item.title}</td>
                <td>
                  <span className={`${styles.pill} ${item.is_open ? styles.pillOpen : styles.pillDone}`}>
                    {complaintStatusLabel(item)}
                  </span>
                </td>
                <td>{complaintWaitLabel(item)}</td>
                <td className={styles.muted}>{formatShortDate(item.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {reporting && (
        <ReportComplaintModal
          defaultCustomerNo={customerNo}
          customers={[{ customer_no: customerNo, name: customerName }]}
          surface="customer_detail"
          onClose={() => setReporting(false)}
          onFiled={() => setVersion((v) => v + 1)}
        />
      )}
    </div>
  );
}
